import React from 'react';
import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import { colors } from '../styles';
import { normalizeCategoryColor } from '../utils/categoryColors';

const BudgetCategoryMiniCard = ({
  name,
  budgeted = 0,
  spent = 0,
  icon = null,
  color = null,
  width = 220,
  height = 280
}) => {
  const theme = useTheme();
  const categoryColor = normalizeCategoryColor(color);
  const budgetedNum = Number(budgeted) || 0;
  const spentNum = Number(spent) || 0;
  const available = Math.max(budgetedNum - spentNum, 0);
  const actualPct = budgetedNum > 0 ? (spentNum / budgetedNum) * 100 : 0;
  const spentPct = budgetedNum > 0 ? Math.min(actualPct, 100) : 0;
  const progressColor = actualPct > 100 ? colors.status.error : colors.status.success;

  // Extracted style constants for maintainability
  const circularBaseSx = {
    position: 'absolute',
    top: 0,
    left: 0,
    '& .MuiCircularProgress-circle': { strokeLinecap: 'round' }
  };
  const overlayCenterBoxSx = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 3
  };

  return (
    <Box
      sx={{
        width,
        height,
        minWidth: width,
        minHeight: height,
        maxWidth: width,
        maxHeight: height,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        py: 2,
        px: 1,
        mx: 'auto',
        bgcolor: 'background.paper',
        borderRadius: '20px',
        boxShadow: 'none'
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 700, textAlign: 'center', mb: 0.5 }}>
        {name}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mb: 1 }}>
        Budget: ${budgetedNum.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </Typography>
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
        <Box sx={{ position: 'relative', width: 200, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
          <CircularProgress
            variant="determinate"
            value={100}
            size={200}
            thickness={3}
            sx={{
              color: theme.palette.grey[200],
              zIndex: 1,
              ...circularBaseSx
            }}
          />
          <CircularProgress
            variant="determinate"
            value={spentPct}
            size={200}
            thickness={3}
            sx={{
              color: progressColor,
              zIndex: 2,
              ...circularBaseSx
            }}
          />
          <Box sx={overlayCenterBoxSx}>
            <Box
              sx={{
                width: 46,
                height: 46,
                mb: 1,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: categoryColor,
                backgroundColor: `${categoryColor}16`,
                border: `1px solid ${categoryColor}36`,
                '& svg': {
                  fontSize: 28
                }
              }}
            >
              {icon}
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: colors.status.success, mt: 1, mb: 0 }}>
              ${available.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
              to spend
            </Typography>
            <Typography variant="body2" sx={{ color: colors.status.error, fontWeight: 500, mb: 0 }}>
              ${spentNum.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              spent
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BudgetCategoryMiniCard;
